import Solver from 'javascript-lp-solver';

const NUMERIC_TOL = 1e-6;
const LAMBDA_SUM_TOL = 1e-5;
const PEER_THRESHOLD = 0.001;

// javascript-lp-solver is not a robust simplex: it has no anti-cycling pivot
// rule and reports `feasible: false` (returning the current, non-optimal
// tableau) when it detects a repeated pivot pattern. Envelopment DEA models
// are highly degenerate, so every DMU is solved in up to three mathematically
// equivalent presentations of the same LP — row scaling and variable order
// change the pivot path but not the feasible set — and only solutions that
// validate against the DEA constraints are reported.
export function buildEnvelopmentModel(dmus, dmuIndex, { isInputOriented, isCCR, variant = 'base' }) {
  const dmuK = dmus[dmuIndex];
  const numInputs = dmuK.inputs.length;
  const numOutputs = dmuK.outputs.length;
  const objective = isInputOriented ? 'theta' : 'phi';
  const scaled = variant !== 'base';
  const selfFirst = variant === 'scaledSelfFirst';
  const variableOrder = selfFirst
    ? [dmuIndex, ...dmus.map((_, j) => j).filter((j) => j !== dmuIndex)]
    : dmus.map((_, j) => j);

  // Dividing a constraint row by a positive constant (the DMU's own value)
  // leaves the feasible set unchanged but de-scales the coefficients.
  const inputScale = (i) => (scaled && dmuK.inputs[i] !== 0 ? 1 / dmuK.inputs[i] : 1);
  const outputScale = (r) => (scaled && dmuK.outputs[r] !== 0 ? 1 / dmuK.outputs[r] : 1);

  // Envelopment forms solved per DMU K (λ_j >= 0, plus Σλ = 1 for BCC/VRS):
  //
  //   input-oriented : min θ  s.t.  Σλx <= θ·x_K    and  Σλy >= y_K
  //   output-oriented: max φ  s.t.  Σλx <= x_K      and  Σλy >= φ·y_K
  //
  // The radial term belongs to the input constraints (θ) in the input-oriented
  // model and to the output constraints (φ) in the output-oriented model.
  const model = {
    optimize: objective,
    opType: isInputOriented ? 'min' : 'max',
    constraints: {},
    variables: {},
  };

  for (let i = 0; i < numInputs; i++) {
    model.constraints[`in_${i}`] = isInputOriented
      ? { max: 0 }
      : { max: dmuK.inputs[i] * inputScale(i) };
  }
  for (let r = 0; r < numOutputs; r++) {
    model.constraints[`out_${r}`] = isInputOriented
      ? { min: dmuK.outputs[r] * outputScale(r) }
      : { min: 0 };
  }
  if (!isCCR) {
    model.constraints.convexity = { equal: 1 };
  }

  variableOrder.forEach((j) => {
    const dmuJ = dmus[j];
    const lambda = { [objective]: 0, convexity: 1 };
    for (let i = 0; i < numInputs; i++) {
      lambda[`in_${i}`] = dmuJ.inputs[i] * inputScale(i);
    }
    for (let r = 0; r < numOutputs; r++) {
      lambda[`out_${r}`] = dmuJ.outputs[r] * outputScale(r);
    }
    model.variables[`lambda_${j}`] = lambda;
  });

  if (isInputOriented) {
    const theta = { theta: 1 };
    for (let i = 0; i < numInputs; i++) {
      theta[`in_${i}`] = -dmuK.inputs[i] * inputScale(i);
    }
    model.variables.theta = theta;
  } else {
    const phi = { phi: 1 };
    for (let r = 0; r < numOutputs; r++) {
      phi[`out_${r}`] = -dmuK.outputs[r] * outputScale(r);
    }
    model.variables.phi = phi;
  }

  return model;
}

export function solveEnvelopment(dmus, dmuIndex, { isInputOriented, isCCR, variant }) {
  const solution = Solver.Solve(
    buildEnvelopmentModel(dmus, dmuIndex, { isInputOriented, isCCR, variant })
  );

  const radial = solution.feasible
    ? (isInputOriented ? solution.theta : solution.phi)
    : undefined;

  const flow = dmus.map((_, j) => {
    const lambda = solution[`lambda_${j}`];
    return Number.isFinite(lambda) ? lambda : 0;
  });

  return { variant, solution, radial, flow, result: solution.result };
}

// Accept a solve only if the reported values form a genuine solution of the
// envelopment LP; otherwise return the reason it was rejected. The library can
// return phantom values from a stopped tableau alongside `feasible: false`.
export function validateSolution(dmus, dmuIndex, { isInputOriented, isCCR }, { solution, radial, flow, result }) {
  const dmuK = dmus[dmuIndex];

  if (solution.feasible !== true) return 'solver reported infeasible';
  if (!Number.isFinite(radial)) return 'radial value missing';
  if (!Number.isFinite(result)) return 'objective unbounded';
  if (Math.abs(result - radial) > NUMERIC_TOL) return 'objective inconsistent with radial';
  if (isInputOriented) {
    if (!(radial > 0) || radial > 1 + NUMERIC_TOL) return `theta out of range (${radial})`;
  } else if (radial < 1 - NUMERIC_TOL) {
    return `phi out of range (${radial})`;
  }
  if (flow.some((lambda) => !Number.isFinite(lambda) || lambda < -NUMERIC_TOL)) {
    return 'lambda negative or non-finite';
  }

  const sumLambda = flow.reduce((sum, lambda) => sum + lambda, 0);
  if (!isCCR && Math.abs(sumLambda - 1) > LAMBDA_SUM_TOL) {
    return `sum of lambda is ${sumLambda}`;
  }

  for (let i = 0; i < dmuK.inputs.length; i++) {
    const lhs = flow.reduce((sum, lambda, j) => sum + lambda * dmus[j].inputs[i], 0);
    if (lhs > dmuK.inputs[i] + NUMERIC_TOL * Math.max(1, Math.abs(dmuK.inputs[i]))) {
      return `input constraint ${i} violated`;
    }
  }
  for (let r = 0; r < dmuK.outputs.length; r++) {
    const lhs = flow.reduce((sum, lambda, j) => sum + lambda * dmus[j].outputs[r], 0);
    const required = (isInputOriented ? 1 : radial) * dmuK.outputs[r];
    if (lhs < required - NUMERIC_TOL * Math.max(1, Math.abs(dmuK.outputs[r]))) {
      return `output constraint ${r} violated`;
    }
  }

  return null;
}

function isBetterSolution(candidate, incumbent, isInputOriented) {
  if (!incumbent) return true;
  return isInputOriented
    ? candidate.radial < incumbent.radial
    : candidate.radial > incumbent.radial;
}

// Try the base formulation and the row-scaled reformulation; keep the better of
// the valid solves. Fall back to the self-first scaled variant only when both
// fail to produce a valid solution.
export function solveDMU(dmus, dmuIndex, { isInputOriented, isCCR }) {
  const failures = [];
  let best = null;

  for (const variant of ['base', 'scaled']) {
    const solved = solveEnvelopment(dmus, dmuIndex, { isInputOriented, isCCR, variant });
    const reason = validateSolution(dmus, dmuIndex, { isInputOriented, isCCR }, solved);
    if (reason) {
      failures.push({ variant, reason });
    } else if (isBetterSolution(solved, best, isInputOriented)) {
      best = solved;
    }
  }

  if (!best) {
    const solved = solveEnvelopment(dmus, dmuIndex, { isInputOriented, isCCR, variant: 'scaledSelfFirst' });
    const reason = validateSolution(dmus, dmuIndex, { isInputOriented, isCCR }, solved);
    if (reason) {
      failures.push({ variant: 'scaledSelfFirst', reason });
    } else {
      best = solved;
    }
  }

  return { solution: best, failures };
}

// Efficiency is reported on a single 0..1 scale: θ for input-oriented and
// 1/φ for output-oriented (φ >= 1), so 1.0000 always marks the frontier.
export function computeDEA(dmus, modelType = 'BCC', orientation = 'input') {
  if (!Array.isArray(dmus) || dmus.length === 0) return [];

  const numInputs = dmus[0].inputs.length;
  const numOutputs = dmus[0].outputs.length;
  const isInputOriented = orientation === 'input';
  const isCCR = modelType === 'CCR';

  return dmus.map((dmuK, dmuIndex) => {
    const { solution, failures } = solveDMU(dmus, dmuIndex, { isInputOriented, isCCR });

    // Never report a solve that did not validate: a fabricated 1.0000 would
    // mark an arbitrary DMU as efficient and its targets would be meaningless.
    if (!solution) {
      console.warn(
        `DEA solver could not solve ${dmuK.name} (${failures
          .map(({ variant, reason }) => `${variant}: ${reason}`)
          .join('; ')})`
      );
      return {
        dmu: dmuK.name,
        efficiency: null,
        isEfficient: false,
        solverFailed: true,
        peers: [],
        slacks: {
          inputs: dmuK.inputs.map(() => '0.0000'),
          outputs: dmuK.outputs.map(() => '0.0000'),
        },
        targets: {
          inputs: dmuK.inputs.map((value) => value.toFixed(4)),
          outputs: dmuK.outputs.map((value) => value.toFixed(4)),
        },
      };
    }

    const { radial, flow } = solution;

    const peers = [];
    flow.forEach((lambda, j) => {
      if (lambda > PEER_THRESHOLD) {
        peers.push({ name: dmus[j].name, lambda });
      }
    });

    // The projected point is the λ-weighted combination of the reference set,
    // so it satisfies Σλx <= x_K and Σλy >= φ·y_K by construction. Targets may
    // never move a DMU backwards — inputs cannot rise and outputs cannot fall,
    // regardless of orientation. For a validated solve the guards are exact
    // no-ops; they absorb solver rounding in the reported projection.
    const targetInputs = [];
    const targetOutputs = [];
    const inputSlacks = [];
    const outputSlacks = [];

    for (let i = 0; i < numInputs; i++) {
      const actual = dmuK.inputs[i];
      const projected = flow.reduce((sum, lambda, j) => sum + lambda * dmus[j].inputs[i], 0);
      const target = Math.min(actual, projected);
      const radialValue = isInputOriented ? radial * actual : actual;

      targetInputs.push(target.toFixed(4));
      inputSlacks.push(Math.max(0, radialValue - target).toFixed(4));
    }

    for (let r = 0; r < numOutputs; r++) {
      const actual = dmuK.outputs[r];
      const projected = flow.reduce((sum, lambda, j) => sum + lambda * dmus[j].outputs[r], 0);
      const target = Math.max(actual, projected);
      const radialValue = isInputOriented ? actual : radial * actual;

      targetOutputs.push(target.toFixed(4));
      outputSlacks.push(Math.max(0, target - radialValue).toFixed(4));
    }

    const efficiency = isInputOriented ? radial : 1 / radial;

    return {
      dmu: dmuK.name,
      efficiency: efficiency.toFixed(4),
      isEfficient: Math.abs(efficiency - 1) < 0.001,
      solverFailed: false,
      peers,
      slacks: { inputs: inputSlacks, outputs: outputSlacks },
      targets: { inputs: targetInputs, outputs: targetOutputs },
    };
  });
}
